import { h } from 'preact';
import { useState } from 'preact/hooks';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

/**
 * ask_user form, port of reactdashboard's CopilotRail/AskUser.tsx: text,
 * multiline text, multiple_choice and boolean questions; required gating;
 * optional dismiss button (used by the thumbs-down feedback follow-up).
 */
export default function AskUser(props) {
  const {
    request,
    title,
    submitLabel = 'Submit',
    submittedLabel = '✓ Answers submitted',
    onDismiss,
    dismissLabel = 'Skip',
  } = props;
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setAnswer = (key, value) => setAnswers(current => ({ ...current, [key]: value }));
  const missingRequired = request.questions.some(
    (question, index) => question.required === true && !(answers[String(index)] || '').trim()
  );

  const submit = async () => {
    if (submitting || submitted) return;
    setSubmitting(true);
    try {
      await request.submit(
        Object.fromEntries(
          request.questions.map((_, index) => [String(index), answers[String(index)] || ''])
        )
      );
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Typography variant="body2" color="primary">
        {submittedLabel}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title || (request.questions.length === 1 ? 'One question for you' : 'Questions for you')}
      </Typography>
      {request.questions.map((question, index) => {
        const key = String(index);
        const content = String(question.content || question.question || 'Question');
        const options = Array.isArray(question.options) ? question.options : [];
        const labelId = `ask-user-${request.id}-${key}`;
        const select = items => (
          <FormControl size="small" fullWidth>
            <InputLabel id={labelId}>Select an answer</InputLabel>
            <Select
              labelId={labelId}
              value={answers[key] || ''}
              label="Select an answer"
              onChange={event => setAnswer(key, String(event.target.value))}
            >
              <MenuItem value="">Select an answer</MenuItem>
              {items.map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

        let control;
        if (question.qtype === 'multiple_choice' && options.length) {
          control = select(
            options.map(option => ({
              value: typeof option === 'string' ? option : String(option.value ?? option.name ?? ''),
              label: typeof option === 'string' ? option : String(option.name ?? option.value ?? ''),
            }))
          );
        } else if (question.qtype === 'boolean') {
          control = select([
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]);
        } else {
          control = (
            <TextField
              size="small"
              fullWidth
              multiline={question.multiline === true}
              minRows={question.multiline === true ? 3 : undefined}
              placeholder={typeof question.placeholder === 'string' ? question.placeholder : undefined}
              value={answers[key] || ''}
              onChange={event => setAnswer(key, event.target.value)}
            />
          );
        }

        return (
          <Box key={key} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              {content}
            </Typography>
            {control}
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
        {onDismiss && (
          <Button size="small" onClick={onDismiss} disabled={submitting}>
            {dismissLabel}
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={submit}
          disabled={submitting || missingRequired}
        >
          {submitting ? 'Submitting…' : submitLabel}
        </Button>
      </Box>
    </Box>
  );
}
